import React, {
  memo, useCallback, useMemo, useRef, useState,
} from 'react'

import { Button, classnames, FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, func, shape, string,
} from 'prop-types'

import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'
import { DEFAULT_CATEGORY } from 'Pods/Shared/GalleryFilters/constants'

import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'
import useIsomorphicLayoutEffect from 'Utils/useIsomorphicLayoutEffect'
import useMediaQuery from 'Utils/useMediaQuery'

import getVisibleCount from './getVisibleCount'

// Matches the `gap` of `.filter-categories` in _community_gallery_panel.scss.
const CHIP_GAP = 8
// Fixed space reserved for the More trigger when anything overflows. The real
// trigger renders ~80px; a small margin keeps the last chip from touching it.
const MORE_WIDTH_RESERVE = 88
const MOBILE_QUERY = '(max-width: 500px)'

// The chip row is content-sized so the search input can absorb the row's slack,
// which means the container's own width only ever reports what the visible chips
// already occupy. Feeding that back into the split would walk the row into
// "More" one chip per pass, since dropping a chip shrinks the very width the
// next pass measures against. Measure the room the row can actually give the
// chips instead: the row less each sibling's flex-basis - its floor - and the
// gaps between them. Reading the basis and gap off the cascade keeps the row's
// sizing in the consuming page's SCSS, where MainFilters layout belongs.
// Siblings sized `auto` have no readable floor, so they fall back to their
// current width; the row then reports only the chips' own width, as it did
// before this split existed.
const getAvailableWidth = (container) => {
  const row = container.parentElement
  if (!row) return container.getBoundingClientRect().width

  const gap = parseFloat(window.getComputedStyle(row).columnGap) || 0
  const siblings = Array.from(row.children).filter(node => node !== container)

  return siblings.reduce((available, sibling) => {
    const basis = parseFloat(window.getComputedStyle(sibling).flexBasis)
    const reserved = Number.isNaN(basis) ? sibling.getBoundingClientRect().width : basis

    return available - reserved - gap
  }, row.getBoundingClientRect().width)
}

function Categories({
  categories = EMPTY_ARRAY, selected = '', onChange = NOOP, morePlaceholder = 'More',
}) {
  const isMobile = useMediaQuery(MOBILE_QUERY)
  const containerRef = useRef(null)
  const [visibleCount, setVisibleCount] = useState(categories.length)

  // Every chip stays mounted: overflowing ones are lifted out of the row with the
  // `--measure-only` modifier (absolute + visibility:hidden) but keep a layout
  // box, so their true width is always measurable. Measuring live here - rather
  // than caching one early measurement - lets the split self-correct whenever the
  // row widens or the chips' width changes (e.g. web fonts landing after mount).
  // The old single-shot approach committed inflated pre-font widths and could
  // never recover, because hidden chips left the DOM and the flex container's
  // width never changed to re-fire the observer.
  //
  // Chips are always the first `categories.length` children; the More trigger,
  // when present, renders after them, so reading by position skips it.
  const chipCount = categories.length
  const recompute = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const chipNodes = Array.from(container.children).slice(0, chipCount)
    if (chipNodes.length === 0) return

    const chipWidths = chipNodes.map(chip => chip.getBoundingClientRect().width)
    setVisibleCount(getVisibleCount({
      chipWidths,
      moreWidth: MORE_WIDTH_RESERVE,
      available: getAvailableWidth(container),
      gap: CHIP_GAP,
    }))
  }, [chipCount])

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    const observer = new ResizeObserver(recompute)
    observer.observe(container)
    // The content-sized container no longer tracks the row, so watch the row
    // itself for the resizes that change how much room the chips have.
    if (container.parentElement) observer.observe(container.parentElement)
    Array.from(container.children).slice(0, chipCount).forEach(chip => observer.observe(chip))

    recompute()

    return () => observer.disconnect()
  }, [chipCount, recompute])

  const moreCategories = useMemo(
    () => categories.slice(visibleCount),
    [categories, visibleCount],
  )

  const isMoreSelected = useMemo(
    () => moreCategories.some(category => category.value === selected),
    [moreCategories, selected],
  )

  const moreFiltersConfig = useMemo(() => [{
    label: null,
    filters: moreCategories.map(({ name, value }) => ({ value, label: name })),
  }], [moreCategories])

  const selectedFilters = useMemo(() => [selected], [selected])

  const handleChange = useCallback((_, value) => {
    onChange(value)
  }, [onChange])

  const moreContent = useMemo(() => (
    <FiltersSidebar
      isCollapsible={false}
      filters={moreFiltersConfig}
      selectedFilters={selectedFilters}
      onFiltersChange={handleChange}
      isControlled
      hideLastDivider
      hideShowMore
      baseFormWrapperClassName="filter-wrapper__content"
      useNewCheckboxes
      useRadio
    />
  ), [moreFiltersConfig, selectedFilters, handleChange])

  const allFiltersConfig = useMemo(() => [{
    label: null,
    filters: categories.map(({ name, value }) => ({ value, label: name })),
  }], [categories])

  const mobileContent = useMemo(() => (
    <FiltersSidebar
      isCollapsible={false}
      filters={allFiltersConfig}
      selectedFilters={selectedFilters}
      onFiltersChange={handleChange}
      isControlled
      hideLastDivider
      hideShowMore
      baseFormWrapperClassName="filter-wrapper__content"
      useNewCheckboxes
      useRadio
    />
  ), [allFiltersConfig, selectedFilters, handleChange])

  const selectedCategory = useMemo(
    () => categories.find(category => category.value === selected) || categories[0],
    [categories, selected],
  )

  if (isMobile && categories.length > 0) {
    return (
      <div className="filter-categories filter-categories--mobile">
        <FiltersWrapper
          placeholder={selectedCategory ? selectedCategory.name : ''}
          content={mobileContent}
          // Only show the filled (cyan) trigger once a non-default category is picked.
          // The select always has a value ("All" by default), so gate on selected != default
          // -- mirrors how useGalleryModels treats `category === default` as "no filter".
          hasSelectedAnyFilters={selected !== DEFAULT_CATEGORY}
          positionHorizontal="left"
          closingDropdownClassName="filter-wrapper filter-categories__more"
          size="medium"
        />
      </div>
    )
  }

  return (
    <div className="filter-categories" ref={containerRef}>
      {categories.map(({ name, value }, index) => (
        <Button
          key={value}
          type="secondary"
          size="medium"
          isActive={value === selected}
          customClass={classnames(
            'filter-categories__chip',
            value === selected && 'filter-categories__chip--active',
            index >= visibleCount && 'filter-categories__chip--measure-only',
          )}
          onClick={() => onChange(value)}
        >
          {name}
        </Button>
      ))}
      {moreCategories.length > 0 && (
        <FiltersWrapper
          placeholder={morePlaceholder}
          content={moreContent}
          hasSelectedAnyFilters={isMoreSelected}
          positionHorizontal="left"
          closingDropdownClassName="filter-wrapper filter-categories__more"
          size="medium"
        />
      )}
    </div>
  )
}

Categories.propTypes = {
  categories: arrayOf(shape({
    name: string.isRequired,
    value: string.isRequired,
  })),
  selected: string,
  onChange: func,
  morePlaceholder: string,
}

export default memo(Categories)
